import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import Button from "components/Button";

import { useDealSidepanel } from "entities/Deal/components/Sidepanel/useDealSidepanel";
import {
  TABS,
  TASKS_TAB_SUBTABS,
} from "entities/Deal/components/Sidepanel/utils";
import { IField } from "entities/Field/sdk";
import { usePackageDealDetails } from "entities/Package/sdk";
import { ITaskChannel } from "entities/Task/sdk";

interface IDueDiligenceFieldOpenTaskButton {
  company_field: IField;
  taskChannels: Array<ITaskChannel>;
  dealId: number;
  tabId: number;
  packageId: number;
  sectionId: number;
  categoryId: number;
  taskButtonDataTestid?: string;
  taskMessagesButtonDataTestid?: string;
}

const DueDiligenceFieldOpenTaskButton = ({
  company_field,
  taskChannels,
  dealId,
  tabId,
  packageId,
  sectionId,
  categoryId,
  taskButtonDataTestid,
  taskMessagesButtonDataTestid,
}: IDueDiligenceFieldOpenTaskButton) => {
  const { show } = useDealSidepanel();

  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId: string;
  }>();

  const queryParams = useMemo(() => {
    if (!_.isNil(params.tabId)) {
      return { tab_id: params.tabId };
    }
    return undefined;
  }, [params.tabId]);

  const { mutate: updatePackage } = usePackageDealDetails({
    packageId,
    dealId,
    queryParams,
  });

  const unreadLabel = useMemo(
    () => _.sumBy(taskChannels, "unread_count"),
    [taskChannels]
  );

  const handleOpenTaskChat = useCallback(() => {
    const taskInitialData = {
      title: company_field.display_name,
      company_package: packageId,
      company_tab: tabId,
      company_section: sectionId,
      company_category: categoryId,
      company_field: company_field.field_id,
    };

    const taskChannelsWithUnreadMessages = _.filter(
      taskChannels,
      (taskChannel) => taskChannel.unread_count > 0
    );

    if (taskChannelsWithUnreadMessages.length === 1) {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: updatePackage,
          subTab: TASKS_TAB_SUBTABS.CHAT,
          taskChannel: taskChannelsWithUnreadMessages[0],
          dealId,
          taskField: company_field,
          taskInitialData,
        },
      });
    } else {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: updatePackage,
          dealId,
          taskField: company_field,
          taskInitialData,
        },
      });
    }
  }, [
    show,
    dealId,
    taskChannels,
    updatePackage,
    company_field,
    packageId,
    tabId,
    categoryId,
    sectionId,
  ]);

  const handleOpenTask = useCallback(() => {
    if (taskChannels.length === 1) {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: updatePackage,
          subTab: TASKS_TAB_SUBTABS.DETAILS,
          taskChannel: taskChannels[0],
          taskField: company_field,
          taskInitialData: {
            title: company_field.display_name,
            company_field: company_field.field_id,
            company_package: packageId,
            company_tab: tabId,
            company_section: sectionId,
            company_category: categoryId,
          },
          dealId,
        },
      });
    } else {
      show({
        meta: {
          tab: TABS.TASKS,
          updateParent: updatePackage,
          dealId,
          taskField: company_field,
          taskInitialData: {
            title: company_field.display_name,
            company_field: company_field.field_id,
            company_package: packageId,
            company_tab: tabId,
            company_section: sectionId,
            company_category: categoryId,
          },
        },
      });
    }
  }, [
    show,
    dealId,
    updatePackage,
    taskChannels,
    company_field,
    packageId,
    tabId,
    categoryId,
    sectionId,
  ]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpenTask}
        data-testid={taskButtonDataTestid}
        sx={{ whiteSpace: "nowrap", minWidth: "min-content" }}
      >
        {taskChannels.length} open task{taskChannels.length > 1 ? "s" : ""}
      </Button>
      {unreadLabel > 0 && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenTaskChat}
          data-testid={taskMessagesButtonDataTestid}
        >
          {unreadLabel} message{unreadLabel > 1 ? "s" : ""}
        </Button>
      )}
    </>
  );
};

export default DueDiligenceFieldOpenTaskButton;
